import React from "react";
import DayCell from "./DayCell";
import { CalendarSummary, buildMonthGrid, formatMonthYear } from "./aggregations";

interface Props {
    summary: CalendarSummary;
    selectedKey: string | null;
    onSelectDay: (isoKey: string) => void;
}

const DAY_HEADERS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function parseIsoKey(k: string): Date {
    const [y, m, d] = k.split("-").map(Number);
    return new Date(y, m - 1, d);
}

const MonthGrid: React.FC<Props> = ({ summary, selectedKey, onSelectDay }) => {
    if (summary.days.length === 0) {
        return null;
    }
    const start = parseIsoKey(summary.days[0].isoKey);
    const end = parseIsoKey(summary.days[summary.days.length - 1].isoKey);

    // Group cells by calendar month so a range that straddles two months
    // renders as two month panels rather than one fused grid.
    const months: Array<{ label: string; weeks: ReturnType<typeof buildMonthGrid> }> = [];
    const cur = new Date(start.getFullYear(), start.getMonth(), 1);
    const last = new Date(end.getFullYear(), end.getMonth(), 1);
    while (cur <= last) {
        const monthStart = new Date(cur.getFullYear(), cur.getMonth(), 1);
        const monthEnd = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
        const weeks = buildMonthGrid(monthStart, monthEnd, summary.byKey);
        months.push({ label: formatMonthYear(monthStart), weeks });
        cur.setMonth(cur.getMonth() + 1);
    }

    const today = new Date();
    const todayKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            {months.map((m) => (
                <div key={m.label}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: "#262626", marginBottom: 8 }}>
                        {m.label}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, marginBottom: 4 }}>
                        {DAY_HEADERS.map((d) => (
                            <div key={d} style={{ textAlign: "center", fontSize: 12, color: "#8c8c8c", padding: "4px 0", fontWeight: 500 }}>
                                {d}
                            </div>
                        ))}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
                        {m.weeks.flat().map((cell) => (
                            <DayCell
                                key={cell.isoKey}
                                date={cell.date}
                                stats={cell.stats}
                                isSelected={selectedKey === cell.isoKey}
                                isToday={cell.isoKey === todayKey}
                                onClick={() => {
                                    if (cell.stats) onSelectDay(cell.isoKey);
                                }}
                            />
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default MonthGrid;
